import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { useAuth } from '../../auth/AuthProvider.jsx';
import { mfaApi } from '../../api/mfa.js';
import { ApiError } from '../../api/client.js';
import { useMfaStepUp } from '../../hooks/useMfaStepUp.js';
import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { Input } from '../ui/Input.jsx';
import { Badge } from '../ui/Badge.jsx';
import { ConfirmDialog } from '../ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../MfaPrompt.jsx';
import { t } from '../../lib/i18n.js';

function QrCanvas({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data) return;
    QRCode.toCanvas(ref.current, data, { width: 192, margin: 1, color: { dark: '#1c1d1f', light: '#ffffff' } }, (err) => {
      if (err) console.error('QR error', err);
    });
  }, [data]);
  return <canvas ref={ref} className="rounded-md border border-border bg-surface-elevated" />;
}

export function MfaSection() {
  const { user, refresh } = useAuth();
  const enabled = Boolean(user?.mfaEnabled);

  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);

  const { run, mfaProps } = useMfaStepUp();

  const enrollMutation = useMutation({
    mutationFn: () => mfaApi.enroll(),
    onSuccess: (data) => {
      setEnrollment(data);
      setCode('');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const verifyMutation = useMutation({
    mutationFn: () => mfaApi.verifyEnrollment(code.trim()),
    onSuccess: async () => {
      toast.success('MFA attivata.');
      setEnrollment(null);
      setCode('');
      await refresh();
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.code === 'MFA_INVALID' || err.code === 'VALIDATION_ERROR')) {
        toast.error('Codice non valido o scaduto.');
      } else {
        toast.error(err instanceof ApiError ? err.message : t('common.error'));
      }
    }
  });

  const disableMutation = useMutation({
    mutationFn: () => run(() => mfaApi.disable()),
    onSuccess: async () => {
      toast.success('MFA disattivata.');
      setConfirmDisable(false);
      await refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  return (
    <Card className="max-w-2xl">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Autenticazione a due fattori (MFA)</h2>
          <p className="text-xs text-ink-muted">Aggiunge un codice temporaneo generato da un'app come Google Authenticator o 1Password.</p>
        </div>
        <Badge tone={enabled ? 'success' : 'warning'}>{enabled ? 'Attiva' : 'Disattivata'}</Badge>
      </header>

      {enabled ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">
            L'MFA è già configurata su questo account. Le azioni sensibili (eliminare utenti, ruotare chiavi, attivare manutenzione)
            richiedono una verifica recente.
          </p>
          <div>
            <Button variant="danger" onClick={() => setConfirmDisable(true)}>Disattiva MFA</Button>
          </div>
        </div>
      ) : enrollment ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted">
            Scansiona il QR con l'app di autenticazione, oppure inserisci il codice segreto manualmente. Poi inserisci il codice a 6 cifre per confermare.
          </p>
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <QrCanvas data={enrollment.otpAuthUrl} />
            <div className="flex flex-col gap-2 text-sm">
              <div>
                <span className="block text-xs text-ink-muted">Issuer</span>
                <span className="font-medium text-ink">{enrollment.issuer}</span>
              </div>
              <div>
                <span className="block text-xs text-ink-muted">Codice segreto</span>
                <code className="break-all rounded bg-border/40 px-2 py-0.5 text-xs text-ink">{enrollment.secret}</code>
              </div>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Input
              label="Codice a 6 cifre"
              className="w-44"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
            <Button onClick={() => verifyMutation.mutate()} loading={verifyMutation.isPending} disabled={code.trim().length < 6}>
              Conferma e attiva
            </Button>
            <Button variant="ghost" onClick={() => { setEnrollment(null); setCode(''); }} disabled={verifyMutation.isPending}>
              Annulla
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">
            Non hai ancora configurato l'MFA. Senza di essa non puoi eseguire operazioni sensibili (cancellare utenti, modificare ruoli di sistema, attivare la manutenzione).
          </p>
          <div>
            <Button onClick={() => enrollMutation.mutate()} loading={enrollMutation.isPending}>Attiva MFA</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDisable}
        title="Disattivare l'MFA?"
        description="Senza MFA non potrai più eseguire le azioni sensibili. Servirà una verifica MFA recente per disattivarla."
        destructive
        confirmLabel="Disattiva"
        loading={disableMutation.isPending}
        onCancel={() => setConfirmDisable(false)}
        onConfirm={() => disableMutation.mutate()}
      />

      <MfaPrompt {...mfaProps} />
    </Card>
  );
}
